import TooltipIcon from '@material-ui/core/TooltipIcon';
import * as React from 'react';
import ButtonBase, {ButtonBaseProps} from '@material-ui/core/ButtonBase';
import * as Colors from 'material-ui/styles/colors';
import {createStyles, Theme, withStyles} from '@material-ui/core/styles';
import * as PropTypes from 'prop-types';
import {WithStyles} from '@material-ui/core/styles/withStyles';
import {getRouterLinkProps} from '@material-ui/core/Button';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import clsx from 'clsx';

const DottedLine = ({stroke, style, classes}) => {
  return (
    <div style={style}>
      {stroke && <div className={classes.dottedLine} />}
      <div className={classes.solidLine} />
    </div>
  );
};

DottedLine.propTypes = {
  stroke: PropTypes.bool,
};

DottedLine.defaultProps = {
  style: {},
  stroke: true,
};

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      flexDirection: 'column',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      fontWeight: 'inherit',
      verticalAlign: 'initial',
      '&:hover': {
        '& $solidLine': {
          width: '100%',
          left: 0,
        },
        '& $openInNewIcon': {
          opacity: 1,
        },
      },
    },
    withOpenInNewIcon: {
      paddingRight: theme.spacing(3),
    },
    dottedLine: {
      margin: 0,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 0,
      borderStyle: 'dotted',
      width: '100%',
      borderTopWidth: 1,
      position: 'relative',
      top: 0,
      borderColor: 'inherit',
    },
    solidLine: {
      borderColor: 'inherit',
      left: '50%',
      margin: 0,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 0,
      borderStyle: 'solid',
      width: 0,
      position: 'relative',
      top: 0,
      borderTopWidth: 1,
      transition: theme.transitions.create('all', {duration: theme.transitions.duration.shortest / 2}),
      '$dottedLine + &': {
        top: -1,
      },
    },
    openInNewIcon: {
      position: 'absolute',
      top: 0,
      right: 0,
      transition: theme.transitions.create('all', {duration: theme.transitions.duration.shortest / 2}),
      opacity: 0,
    },
  });

export const hoverContainerStyles = createStyles({
  inlineButtonHoverContainer: {},
  inlineButtonRoot: {
    '$inlineButtonHoverContainer:not(:hover) &': {
      color: 'inherit!important',
    },
  },
  inlineButtonDottedLine: {
    '$inlineButtonHoverContainer:not(:hover) &': {
      visibility: 'hidden',
    },
  },
});

export interface InlineButtonProps extends ButtonBaseProps {
  label?: React.ReactNode;
  stroke?: boolean;
  href?: string;
  target?: string;
  disableOpenInNewIcon?: boolean;
}

function InlineButton(props: InlineButtonProps & WithStyles<typeof styles>) {
  const {
    children,
    label,
    style,
    stroke,
    href,
    disableOpenInNewIcon,
    classes: {root, openInNewIcon, withOpenInNewIcon: withOpenInNewIconClass, ...otherClasses},
    className,
    ...other
  } = props;

  const computedStyle = {...InlineButton.defaultProps.style, ...style};
  let color = computedStyle.color;
  if (!color) {
    color = Colors.lightGreen700;
  }
  const routerLinkProps = getRouterLinkProps(href);
  const withOpenInNewIcon = !disableOpenInNewIcon && routerLinkProps.target;
  return (
    <ButtonBase
      {...routerLinkProps}
      {...other}
      style={computedStyle}
      className={clsx(className, root, {[withOpenInNewIconClass]: withOpenInNewIcon})}
    >
      {label ? label : children}
      {withOpenInNewIcon ? (
        <TooltipIcon
          fontSize="small"
          color="inherit"
          className={openInNewIcon}
          icon={OpenInNewIcon}
          title="Opens in a new tab"
        />
      ) : (
        undefined
      )}
      <DottedLine
        style={{
          width: '100%',
          borderColor: color,
        }}
        stroke={stroke}
        classes={otherClasses}
      />
    </ButtonBase>
  );
}

InlineButton.defaultProps = {
  style: {color: Colors.lightGreen700},
};

export default withStyles(styles)(InlineButton);
